const CARE_PRODUCTS = new Set(['8517757206852', '8651256430916']);

export function plantDimensions(title) {
  const patterns = [
    /\bpot\s*:?\s*(?:[Øø]\s*)?(\d+(?:[.,]\d+)?)\s*\/\s*(\d+(?:[.,]\d+)?)/i,
    /\bpot\s*:?\s*[Øø]\s*(\d+(?:[.,]\d+)?)\s*(?:cm\s*)?[×x]\s*H\s*(\d+(?:[.,]\d+)?)/i
  ];
  const match = patterns.map(pattern => String(title).match(pattern)).find(Boolean);
  if (!match) return null;
  const diameter = Number(match[1].replace(',', '.'));
  const height = Number(match[2].replace(',', '.'));
  return diameter > 0 && height > 0 ? { diameter, height } : null;
}

export function potDimensions(title) {
  const opening = String(title).match(/\bouverture\s*:?\s*(\d+(?:[.,]\d+)?)/i);
  const depth = String(title).match(/\bprofondeur\s*:?\s*(\d+(?:[.,]\d+)?)/i);
  if (!opening || !depth) return null;
  const values = { opening: Number(opening[1].replace(',', '.')), depth: Number(depth[1].replace(',', '.')) };
  return values.opening > 0 && values.depth > 0 ? values : null;
}

export function fitsPot(plant, pot) {
  if (!plant || !pot) return false;
  const gap = pot.opening - plant.diameter;
  return gap >= 1 && gap <= Math.max(5, plant.diameter * 0.25) && pot.depth - plant.height >= 0.5;
}

export function choosePot(plant, options, currentId = '') {
  const compatible = options.filter(option => !option.disabled && fitsPot(plant, potDimensions(option.title)));
  const retained = compatible.find(option => option.id === currentId);
  if (retained) return retained;
  return compatible.sort((a, b) => {
    const pa = potDimensions(a.title);
    const pb = potDimensions(b.title);
    // Prefer 2 cm of clearance; a verified 1 cm clearance remains usable.
    return Math.abs(pa.opening - plant.diameter - 2) - Math.abs(pb.opening - plant.diameter - 2) || pa.opening - pb.opening || pa.depth - pb.depth;
  })[0] || null;
}

const ElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class NaturaPotFit extends ElementBase {
  connectedCallback() {
    if (this.started) return;
    const data = this.querySelector('script[type="application/json"]');
    if (!data) return;
    try {
      this.config = JSON.parse(data.textContent);
    } catch {
      return;
    }
    this.started = true;
    this.status = this.querySelector('[role="status"]');
    this.previousVariantId = null;
    this.originalDisabled = new WeakMap();
    this.cardPlantKeys = new WeakMap();
    this.schedule = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    };
    this.guard = event => {
      const form = document.getElementById('AddToCartForm');
      if (!form || !form.contains(event.target)) return;
      if (event.type === 'click' && !event.target.closest('#AddToCart')) return;
      if (!this.sync()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.announce(this.config.selectionUpdated);
      }
    };
    document.addEventListener('change', this.schedule, true);
    document.addEventListener('click', this.guard, true);
    document.addEventListener('submit', this.guard, true);
    this.timer = setInterval(this.schedule, 250);
    this.sync();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    cancelAnimationFrame(this.frame);
    document.removeEventListener('change', this.schedule, true);
    document.removeEventListener('click', this.guard, true);
    document.removeEventListener('submit', this.guard, true);
    this.started = false;
  }

  announce(message) {
    const status = this.status;
    if (status && status.textContent !== message) status.textContent = message;
  }

  sync() {
    const form = document.getElementById('AddToCartForm');
    if (!form) return true;
    const widget = form.querySelector('lb-upsell-widget');
    if (widget && this.status && this.status.previousElementSibling !== widget) widget.after(this.status);
    const plantSelect = form.querySelector('select[name="id"], input[name="id"]');
    const variantId = plantSelect ? String(plantSelect.value) : '';
    const variant = this.config.variants.find(item => String(item.id) === variantId);
    const plant = variant && variant.available ? plantDimensions(variant.title) : null;
    const addButton = form.querySelector('#AddToCart');
    const buttonLabel = addButton && Array.from(addButton.children).find(child => child.tagName === 'SPAN' && !child.classList.contains('sr-only'));
    if (variant?.available && addButton && !addButton.disabled && buttonLabel && this.config.soldOut && buttonLabel.textContent.trim() === this.config.soldOut) {
      buttonLabel.textContent = this.config.addToCart;
    }
    if (variant && !variant.available && addButton?.disabled && buttonLabel && this.config.soldOut && buttonLabel.textContent.trim() === this.config.addToCart) {
      buttonLabel.textContent = this.config.soldOut;
    }
    const initial = this.previousVariantId === null;
    const changed = !initial && this.previousVariantId !== variantId;
    this.previousVariantId = variantId;
    let safeToAdd = true;
    for (const card of form.querySelectorAll('lb-addon .lb-product-card[lb-product-id]')) {
      if (CARE_PRODUCTS.has(card.getAttribute('lb-product-id'))) continue;
      const picker = card.querySelector('select.lb-variant-picker');
      const checkbox = card.querySelector('input.lb-card-checkbox');
      const options = picker ? Array.from(picker.options).map(option => {
        if (!this.originalDisabled.has(option)) this.originalDisabled.set(option, option.disabled);
        return { id: option.value, title: option.textContent, disabled: this.originalDisabled.get(option), element: option };
      }) : [];
      const plantKey = plant ? variantId : '';
      const firstForPlant = this.cardPlantKeys.get(card) !== plantKey;
      const best = choosePot(plant, options, firstForPlant ? '' : picker?.value);
      if (picker && options.length) this.cardPlantKeys.set(card, plantKey);
      const currentFits = best && picker && fitsPot(plant, potDimensions(picker.selectedOptions[0]?.textContent));
      if (checkbox?.checked && (changed || !currentFits)) {
        safeToAdd = false;
        checkbox.click();
        this.announce(this.config.selectionUpdated);
      }
      for (const option of options) {
        const enabled = !option.disabled && fitsPot(plant, potDimensions(option.title));
        if (option.element.disabled === enabled) option.element.disabled = !enabled;
        if (option.element.hidden === enabled) option.element.hidden = !enabled;
      }
      if (best && picker.value !== best.id) {
        picker.value = best.id;
        picker.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const ready = Boolean(best && picker.value === best.id && card.getAttribute('lb-variant-id') === best.id);
      if (ready && checkbox?.checked && safeToAdd) this.announce('');
      const readyValue = ready ? 'true' : 'false';
      if (card.getAttribute('data-natura-pot-ready') !== readyValue) card.setAttribute('data-natura-pot-ready', readyValue);
      if (card.getAttribute('aria-hidden') !== String(!ready)) card.setAttribute('aria-hidden', String(!ready));
    }
    return safeToAdd;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('natura-pot-fit')) {
  customElements.define('natura-pot-fit', NaturaPotFit);
}



